import { fetchWithAuth, type AccessTokenGetter } from "@/lib/utils";

export const FOLLOW_UP_SUGGESTIONS_CONTRACT_VERSION = 1 as const;

export interface FollowUpSuggestion {
  id: string;
  label: string;
  prompt: string;
}

export interface FollowUpSuggestionsEnvelope {
  capability_version: 1;
  message_id: string;
  stored: boolean;
  suggestions: FollowUpSuggestion[];
}

export interface FollowUpSuggestionsCapability {
  follow_up_suggestions: 0 | 1;
}

const nonEmpty = (value: unknown): value is string =>
  typeof value === "string" && value.trim().length > 0;

function record(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? value as Record<string, unknown> : null;
}

export function parseFollowUpSuggestionsCapability(value: unknown): FollowUpSuggestionsCapability {
  const version: unknown = record(value)?.follow_up_suggestions;
  return { follow_up_suggestions: Number.isInteger(version) && version === FOLLOW_UP_SUGGESTIONS_CONTRACT_VERSION ? 1 : 0 };
}

export function parseFollowUpSuggestionsEnvelope(value: unknown, messageId: string): FollowUpSuggestionsEnvelope | null {
  const data = record(value);
  if (!data) return null;
  const version: unknown = data.capability_version;
  const id: unknown = data.message_id;
  const stored: unknown = data.stored;
  const entries: unknown = data.suggestions;
  if (!Number.isInteger(version) || version !== FOLLOW_UP_SUGGESTIONS_CONTRACT_VERSION ||
      !nonEmpty(id) || id !== messageId || typeof stored !== "boolean" || !Array.isArray(entries)) return null;
  const suggestions: FollowUpSuggestion[] = [];
  for (const entry of entries) {
    const candidate = record(entry);
    const candidateId: unknown = candidate?.id;
    const label: unknown = candidate?.label;
    const prompt: unknown = candidate?.prompt;
    if (nonEmpty(candidateId) && nonEmpty(label) && nonEmpty(prompt)) {
      suggestions.push({ id: candidateId, label, prompt });
    }
  }
  return { capability_version: 1, message_id: id, stored, suggestions: suggestions.slice(0, 3) };
}

async function request(url: string, getToken: AccessTokenGetter, signal: AbortSignal, messageId?: string): Promise<unknown | null> {
  try {
    const response = await fetchWithAuth(url, {
      cache: "no-store", signal,
      headers: { ...(messageId ? { "Content-Type": "application/json" } : {}) },
      ...(messageId ? { method: "POST", body: JSON.stringify({ message_id: messageId }) } : {}),
    }, getToken);
    if (!response.ok || signal.aborted) return null;
    return await response.json() as unknown;
  } catch {
    return null;
  }
}

function base(sessionId: string): string {
  return `/api/sessions/${encodeURIComponent(sessionId)}/follow-up-suggestions`;
}

export async function fetchFollowUpSuggestionsCapability(sessionId: string, getToken: AccessTokenGetter, signal: AbortSignal): Promise<FollowUpSuggestionsCapability> {
  return parseFollowUpSuggestionsCapability(await request(`${base(sessionId)}/capabilities`, getToken, signal));
}

export async function readFollowUpSuggestions(sessionId: string, messageId: string, getToken: AccessTokenGetter, signal: AbortSignal): Promise<FollowUpSuggestionsEnvelope | null> {
  const data = await request(`${base(sessionId)}?message_id=${encodeURIComponent(messageId)}`, getToken, signal);
  return signal.aborted ? null : parseFollowUpSuggestionsEnvelope(data, messageId);
}

export async function generateFollowUpSuggestions(sessionId: string, messageId: string, getToken: AccessTokenGetter, signal: AbortSignal): Promise<FollowUpSuggestionsEnvelope | null> {
  const data = await request(base(sessionId), getToken, signal, messageId);
  return signal.aborted ? null : parseFollowUpSuggestionsEnvelope(data, messageId);
}
